'use client';
import Link from 'next/link';
import {
    MapPin,
    Phone,
    Mail,
    ChevronRight,
    Heart,
    PenTool,
    BookOpen,
    Video,
    Users,
    Globe,
    Edit,
    CheckCircle,
    Volume2,
    Layout,
    Type,
    Palette,
    Image,
    FileText,
} from 'lucide-react';
import Logo from '../logo';

const services = [
    {
        label: 'Book Marketing',
        href: '/book-marketing',
        icon: <Users className="h-4 w-4" />,
    },
    {
        label: 'Book Publishing',
        href: '/book-publishing',
        icon: <BookOpen className="h-4 w-4" />,
    },
    {
        label: 'Book Video Trailer',
        href: '/book-video-trailer',
        icon: <Video className="h-4 w-4" />,
    },
    {
        label: 'Book Writing',
        href: '/book-writing',
        icon: <PenTool className="h-4 w-4" />,
    },
    {
        label: "Children's Book Publishing",
        href: '/childrens-book-publishing',
        icon: <BookOpen className="h-4 w-4" />,
    },
    {
        label: 'Fiction Writing',
        href: '/fiction-writing',
        icon: <Edit className="h-4 w-4" />,
    },
    {
        label: 'Non-fiction Writing',
        href: '/non-fiction-writing',
        icon: <FileText className="h-4 w-4" />,
    },
    {
        label: 'SEO Content Writing',
        href: '/seo-content-writing',
        icon: <Globe className="h-4 w-4" />,
    },
    {
        label: 'Proofreading & Editing',
        href: '/proofreading-editing',
        icon: <CheckCircle className="h-4 w-4" />,
    },
    {
        label: 'Article Publishing',
        href: '/article-publishing',
        icon: <FileText className="h-4 w-4" />,
    },
    { label: 'Audiobook', href: '/audiobook', icon: <Volume2 className="h-4 w-4" /> },
    {
        label: 'Author Website Design',
        href: '/author-website-design',
        icon: <Layout className="h-4 w-4" />,
    },
    { label: 'Blog Writing', href: '/blog-writing', icon: <Edit className="h-4 w-4" /> },
    {
        label: 'Book Cover Design',
        href: '/book-cover-design',
        icon: <Palette className="h-4 w-4" />,
    },
    {
        label: 'Book Formatting',
        href: '/book-formatting',
        icon: <Type className="h-4 w-4" />,
    },
    {
        label: 'Book Illustration',
        href: '/book-illustration',
        icon: <Image className="h-4 w-4" />,
    },
    {
        label: 'Website Content Writing',
        href: '/website-content-writing',
        icon: <FileText className="h-4 w-4" />,
    },
];

const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Services', href: '/services' },
    // { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact-us' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-conditions' },
    { label: 'Refund Policy', href: '/refund-policy' },
];

function FooterCol({ title, items, showIcons = false }) {
    return (
        <div>
            <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
            <ul className="space-y-3 list-none">
                {items.map((item) => (
                    <li key={item.label}>
                        <Link
                            href={item.href}
                            className="group flex items-center gap-3 text-sm text-white/70 transition hover:text-white"
                        >
                            {showIcons && item.icon && (
                                <span className="text-primary transition-transform group-hover:scale-110">
                                    {item.icon}
                                </span>
                            )}
                            {!showIcons && (
                                <ChevronRight className="h-3 w-3 text-primary transition-transform group-hover:translate-x-1" />
                            )}
                            <span className="transition group-hover:translate-x-0.5">
                                {item.label}
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ContactInfo() {
    return (
        <div className="w-full bg-white/10 p-4 rounded-md mt-10">
            <div className="max-sm:space-y-2 lg:flex w-full gap-6">
                <div className="flex gap-3 shrink-0">
                    <MapPin className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div>
                        <p className="text-sm text-white/70 mb-0">
                            8201 Greensboro Drive suite 615, McLean VA 22102
                        </p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <Phone className="h-5 w-5 text-primary shrink-0" />
                    <a
                        href="tel:+15408938895"
                        className="text-sm text-white/70 hover:text-white transition"
                    >
                        540-893-8895
                    </a>
                </div>
                <div className="flex  gap-3">
                    <Mail className="h-5 w-5 text-primary shrink-0" />
                    <a
                        href="mailto:info@virginiabookpublisher.com"
                        className="text-sm text-white/70 hover:text-white transition"
                    >
                        info@virginiabookpublisher.com
                    </a>
                </div>
            </div>
        </div>
    );
}

function SocialIcon({ href, label, children }) {
    return (
        <Link
            href={href}
            aria-label={label}
            className="grid h-10 w-10 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 transition hover:bg-white/10 hover:-translate-y-0.5"
        >
            {children}
        </Link>
    );
}

export default function Footer() {
    return (
        <footer className="bg-neutral-900 text-white">
            {/* Top Gradient Line */}
            <div className="h-px w-full bg-linear-to-r from-transparent via-secondary to-transparent" />

            <div className="container px-4 py-12">
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
                    {/* Brand & Description */}
                    <div className="lg:col-span-1">
                        <div className="inline-flex items-center gap-3 mb-6">
                            <div className="w-[140px]">
                                <Logo></Logo>
                            </div>
                        </div>

                        <p className="text-sm text-white/70 mb-6">
                            We empower authors to bring their stories to life with comprehensive
                            publishing, marketing, and design services.
                        </p>

                        <div className="flex gap-3">
                            <SocialIcon
                                href="https://www.facebook.com/virginiabookpublisher"
                                label="Facebook"
                            >
                                <span className="text-white/85">fb</span>
                            </SocialIcon>
                            <SocialIcon href="https://x.com/VirginiaBo32503" label="Twitter/X">
                                <span className="text-white/85">tw</span>
                            </SocialIcon>
                            <SocialIcon
                                href="https://www.linkedin.com/company/virginiabookpublisher"
                                label="LinkedIn"
                            >
                                <span className="text-white/85">in</span>
                            </SocialIcon>
                            {/* <SocialIcon href="#" label="Instagram">
                                <span className="text-white/85">ig</span>
                            </SocialIcon> */}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <FooterCol title="Quick Links" items={quickLinks} />
                    </div>

                    {/* Services (First Half) */}
                    <div>
                        <FooterCol
                            title="Our Services"
                            items={services.slice(0, Math.ceil(services.length / 2))}
                            showIcons={true}
                        />
                    </div>

                    {/* Services (Second Half) & Contact */}
                    <div className="space-y-8">
                        <FooterCol
                            title=""
                            items={services.slice(Math.ceil(services.length / 2))}
                            showIcons={true}
                        />
                    </div>
                </div>
                <ContactInfo />
            </div>

            {/* Bottom Copyright Bar */}
            <div className="bg-neutral-950 py-4">
                <div className="container px-4">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
                        <div className="text-center md:text-left text-sm">
                            <p>
                                © {new Date().getFullYear()} All Rights Reserved
                                {/* | Gray Scale
                                Enterprise */}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <Heart className="h-4 w-4 text-secondary" />
                            <span>Proudly serving authors worldwide</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
